<template>
  <v-container fluid>
    <v-dialog
      max-width="600px"
      v-model="linkProductDialog">
      <v-card>
        <v-card-title>
          Link Product to {{ customerContext ? customerContext.name : 'Customer' }}
        </v-card-title>
        <v-card-text>
          <p class="text-caption mb-4">Select a product from your parent organization to link to this customer.</p>
          <v-select
            v-model="selectedProduct"
            :items="availableParentProducts"
            :label="$tc('product')"
            item-text="name"
            item-value="id"
            return-object
            :loading="loadingParentProducts"
            :no-data-text="loadingParentProducts ? 'Loading...' : 'No products available to link'"
            outlined/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            text
            @click="closeLinkProductDialog">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedProduct"
            :loading="linkingProduct"
            @click="linkProductToCustomer">
            {{ $t('link') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div class="d-flex justify-space-between align-center">
      <h2>{{ $tc('product', 2) }}</h2>
      <div>
        <v-btn
          v-if="userLoaded && user.can('create_product') && isChildCustomer"
          outlined
          color="secondary-900"
          class="mr-2"
          @click="openLinkProductDialog">
          <v-icon class="mr-1 ml-n2">mdi-link-variant</v-icon>
          {{ $t('link_product') }}
        </v-btn>
        <v-btn
          v-if="userLoaded && user.can('create_product')"
          outlined
          color="primary"
          to="/product/create">
          <v-icon class="mr-1 ml-n2">mdi-plus</v-icon>
          {{ $t('new_product') }}
        </v-btn>
      </div>
    </div>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <div class="align-center">
        <table-pagination-controls
          :total-rows="remoteData.totalRows"
          :current-page.sync="remoteData.currentPage"
          :rows-per-page.sync="remoteData.rowsPerPage"
          @update:currentPage="refreshResults"
          @update:rowsPerPage="initiateSearch"
          :per-page-options="remoteData.pageOptions"/>
      </div>
      <div/>
    </div>
    <v-data-table
      hide-default-footer
      class="vf-table route-index-table mt-2"
      :headers="tableFields"
      :items="remoteData.items"
      :server-items-length="remoteData.totalRows"
      :sort-by.sync="remoteData.sortBy"
      :sort-desc.sync="remoteData.sortDesc"
      :loading="remoteData.isBusy"
      :loading-text="$t('loading')"
      @update:sort-by="initiateSearch()"
      @update:sort-desc="initiateSearch()">
      <template #item.name="row">
        <router-link :to="`product/${row.item.id}`">
          {{ row.value }}
        </router-link>
      </template>
      <template #item.zone="row">
        {{ $t(row.value) }}
      </template>
      <template #item.start_temp="row">
        {{ row.item.start_temp.toFixed(1) }} &deg;{{ customerContext.tempFormat() }}
      </template>
      <template #item.min_temp="row">
        {{ row.item.min_temp.toFixed(1) }} &deg;{{ customerContext.tempFormat() }}
      </template>
      <template #item.max_temp="row">
        {{ row.item.max_temp.toFixed(1) }} &deg;{{ customerContext.tempFormat() }}
      </template>
      <template #item.actions="row">
        <vf-tooltip :text="$t('view')">
          <v-btn :to="'/product/' + row.item.id" color="primary" fab text x-small>
            <v-icon>mdi-eye</v-icon>
          </v-btn>
        </vf-tooltip>
        <vf-tooltip v-if="user.can('update_product')" :text="$t('edit')">
          <v-btn :to="'/product/update/' + row.item.id" color="primary" fab text x-small>
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </vf-tooltip>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>

import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import TablePaginationControls from '@/components/shared/TablePaginationControls.vue'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import VfTooltip from '@/components/shared/VfTooltip'

export default {
  name: 'ProductsIndex',
  components: {
    VfTooltip,
    TablePaginationControls
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin
  ],
  props: {
    id: Number
  },
  data () {
    return {
      model: 'Product',
      showErrorDialog: false,
      isLoaded: false,
      linkProductDialog: false,
      selectedProduct: null,
      availableParentProducts: [],
      loadingParentProducts: false,
      linkingProduct: false,
      tableFields: [
        {
          value: 'name',
          text: this.$t('name'),
          sortable: true
        },
        {
          value: 'zone',
          text: this.$t('zone'),
          sortable: true
        },
        {
          value: 'start_temp',
          text: this.$t('default_starting_temperature'),
          sortable: true
        },
        {
          value: 'min_temp',
          text: this.$t('minimum_temperature'),
          sortable: true
        },
        {
          value: 'max_temp',
          text: this.$t('maximum_temperature'),
          sortable: true
        },
        {
          value: 'coefficient',
          text: this.$t('coefficient'),
          sortable: true,
          class: 'root-only',
          cellClass: 'root-only'
        },
        {
          value: 'actions',
          text: this.$tc('action', 2),
          sortable: false
        }
      ]
    }
  },
  computed: {
    isChildCustomer () {
      return this.customerContext && this.customerContext.parent_id != null
    }
  },
  methods: {
    /**
     * Constructs the current search criteria for the remote data table request.
     *
     * @return object
     */
    buildSearchCriteria () {
      const searchCriteria = {
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }

      if (this.remoteData.sortBy !== null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }

      return searchCriteria
    },

    /**
     * Open the link product dialog and fetch available parent products
     */
    openLinkProductDialog () {
      console.log('Opening link product dialog. Customer context:', this.customerContext)
      this.linkProductDialog = true
      this.fetchParentProducts()
    },

    /**
     * Close the link product dialog and reset state
     */
    closeLinkProductDialog () {
      this.linkProductDialog = false
      this.selectedProduct = null
      this.availableParentProducts = []
    },

    /**
     * Fetch products owned by the parent customer using dedicated parent-products endpoint
     * Excludes products already linked to the current customer
     */
    fetchParentProducts () {
      if (!this.customerContext) {
        console.warn('No customer context available')
        return
      }

      if (!this.customerContext.parent_id) {
        console.warn('Customer context has no parent_id. Current customer is likely a parent customer.')
        return
      }

      console.log('Fetching parent products for customer:', this.customerContext.id)
      this.loadingParentProducts = true

      // Use the new parent-products endpoint
      this.$httpAdapter({
        url: 'product/parent-products',
        method: 'GET',
        params: {
          customer_id: this.customerContext.id,
          fields: 'id,name,zone,deleted'
        }
      })
        .then(response => {
          const allProducts = response.data.items || []
          console.log('Parent products received:', allProducts)

          // Filter out deleted products and already-linked products
          this.availableParentProducts = allProducts.filter(product => {
            // Explicitly check deleted field is null
            if (product.deleted !== null && product.deleted !== undefined) {
              console.log(`Product ${product.name} is deleted, excluding`)
              return false
            }

            // Check if this product is already in the customer's product list
            const alreadyLinked = this.remoteData.items.some(item => item.id === product.id)
            if (alreadyLinked) {
              console.log(`Product ${product.name} is already linked, excluding`)
            }
            return !alreadyLinked
          })

          console.log('Available parent products after filtering:', this.availableParentProducts)
        })
        .catch(error => {
          console.error('Error fetching parent products:', error)
          this.handleError(error)
        })
        .finally(() => {
          this.loadingParentProducts = false
        })
    },

    /**
     * Link the selected product to the current customer
     */
    linkProductToCustomer () {
      if (!this.selectedProduct || !this.customerContext) {
        return
      }

      this.linkingProduct = true

      // Create a product record instance to use the linkCustomer method
      const product = this.$orm('Product').createRecord({
        id: this.selectedProduct.id
      })

      product.linkCustomer(this.customerContext.id)
        .then(() => {
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$i18n.tc('product')} "${this.selectedProduct.name}" has been linked successfully`
          )
          this.closeLinkProductDialog()
          this.refreshResults()
        })
        .catch(error => {
          this.handleError(error)
        })
        .finally(() => {
          this.linkingProduct = false
        })
    },

    /**
     * Refresh the results to ensure they match the new customer context
     *
     * @param {CustomerRecord} newCustomerContext
     * @param {CustomerRecord} oldCustomerContext
     */
    customerContextChangeHandler (newCustomerContext, oldCustomerContext) {
      this.initiateSearch()
    }
  },
  created () {
    this.initiateSearch()
  }
}
</script>

<style scoped>
</style>
